import { motion } from "framer-motion"
import { useState } from "react"
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ShoppingCartIcon,
  CursorArrowRaysIcon,
  FaceSmileIcon,
  CheckBadgeIcon,
  TagIcon,
  StarIcon,
  GlobeAltIcon,
  FingerPrintIcon,
  RocketLaunchIcon,
} from "@heroicons/react/24/outline"
import {
  AdChannel,
  MetaObjective,
  TiktokGoal,
  OptimizationGoal,
} from "~/ui-rtk/api/types/custom-attribution-enums"
import { toCurrency } from "~/ui-rtk/utils/number-utils"

const META_OBJECTIVE_ICONS: Record<
  string,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  [MetaObjective.OUTCOME_SALES]: ShoppingCartIcon,
  [MetaObjective.LINK_CLICKS]: CursorArrowRaysIcon,
  [MetaObjective.OUTCOME_ENGAGEMENT]: FaceSmileIcon,
  [MetaObjective.CONVERSIONS]: CheckBadgeIcon,
  [MetaObjective.PRODUCT_CATALOG_SALES]: TagIcon,
  [MetaObjective.OUTCOME_AWARENESS]: StarIcon,
  [MetaObjective.REACH]: GlobeAltIcon,
  [MetaObjective.BRAND_AWARENESS]: FingerPrintIcon,
  [MetaObjective.OUTCOME_TRAFFIC]: RocketLaunchIcon,
}

interface ObjectiveSelectorProps {
  adChannel: AdChannel
  onObjectiveSelect: (objective: MetaObjective) => void
  onGoalSelect: (goal: OptimizationGoal) => void
  error?: string
  touched?: boolean
  adsetData?: Record<string, string | undefined>[]
}

export function ObjectiveSelector({
  adChannel,
  onObjectiveSelect,
  onGoalSelect,
  error,
  touched,
  adsetData = [],
}: ObjectiveSelectorProps) {
  // For Meta: show objectives (which then lead to goal selection)
  // For TikTok: show TikTok goals and call onGoalSelect directly
  const isMeta = adChannel === AdChannel.META

  // State for tracking expanded items
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  // Calculate total spend for this channel to get percentages
  const totalChannelSpend = adsetData
    .filter((row: Record<string, string | undefined>) => {
      const rowChannel =
        row["company_media_metrics_adsets.media_channel"]?.toLowerCase()
      return rowChannel === adChannel?.toLowerCase()
    })
    .reduce((total: number, row: Record<string, string | undefined>) => {
      const spend = parseFloat(row["company_media_metrics_adsets.spend"] || "0")
      return total + spend
    }, 0)

  // Calculate spending for each objective/goal
  const getSpendingForItem = (item: string) => {
    if (!adsetData || adsetData.length === 0) return 0

    // Filter data by the same channel
    const channelData = adsetData.filter(
      (row: Record<string, string | undefined>) => {
        const rowChannel =
          row["company_media_metrics_adsets.media_channel"]?.toLowerCase()
        return rowChannel === adChannel?.toLowerCase()
      },
    )

    if (isMeta) {
      // For Meta: match by objective
      const matchingRows = channelData.filter(
        (row: Record<string, string | undefined>) => {
          const rowObjective = row["company_media_metrics_adsets.objective"]
          return rowObjective === item
        },
      )

      return matchingRows.reduce(
        (total: number, row: Record<string, string | undefined>) => {
          const spend = parseFloat(
            row["company_media_metrics_adsets.spend"] || "0",
          )
          return total + spend
        },
        0,
      )
    }
    // For TikTok: match by optimization_goal ONLY (standalone, ignore objective)
    const matchingRows = channelData.filter(
      (row: Record<string, string | undefined>) => {
        const rowGoal = row["company_media_metrics_adsets.optimization_goal"]
        return rowGoal === item
      },
    )

    return matchingRows.reduce(
      (total: number, row: Record<string, string | undefined>) => {
        const spend = parseFloat(
          row["company_media_metrics_adsets.spend"] || "0",
        )
        return total + spend
      },
      0,
    )
  }

  // Get base items and sort by spending amount (highest first)
  const baseItems = isMeta
    ? Object.values(MetaObjective)
    : Object.values(TiktokGoal)

  const items = baseItems.sort((a, b) => {
    const spendingA = getSpendingForItem(a)
    const spendingB = getSpendingForItem(b)
    return spendingB - spendingA
  })

  // Get campaigns for a specific objective/goal
  const getCampaignsForItem = (item: string) => {
    if (!adsetData || adsetData.length === 0) return []

    // Filter data by the same channel
    const channelData = adsetData.filter(
      (row: Record<string, string | undefined>) => {
        const rowChannel =
          row["company_media_metrics_adsets.media_channel"]?.toLowerCase()
        return rowChannel === adChannel?.toLowerCase()
      },
    )

    let filteredData = []
    if (isMeta) {
      filteredData = channelData.filter(
        (row: Record<string, string | undefined>) => {
          const rowObjective = row["company_media_metrics_adsets.objective"]
          return rowObjective === item
        },
      )
    } else {
      filteredData = channelData.filter(
        (row: Record<string, string | undefined>) => {
          const rowGoal = row["company_media_metrics_adsets.optimization_goal"]
          return rowGoal === item
        },
      )
    }

    // Group by campaign and sum spending
    const campaignMap = new Map()
    filteredData.forEach((row: Record<string, string | undefined>) => {
      const campaignId = row["company_media_metrics_adsets.campaign_id"]
      const campaignName = row["company_media_metrics_adsets.campaign_name"]
      const spend = parseFloat(row["company_media_metrics_adsets.spend"] || "0")

      if (campaignMap.has(campaignId)) {
        campaignMap.get(campaignId).spend += spend
      } else {
        campaignMap.set(campaignId, {
          id: campaignId,
          name: campaignName,
          spend,
        })
      }
    })

    // Filter campaigns with spending > 0 and sort by spend (highest first)
    return Array.from(campaignMap.values())
      .filter(campaign => campaign.spend > 0)
      .sort((a, b) => b.spend - a.spend)
  }

  // Toggle expanded state
  const toggleExpanded = (item: string) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(item)) {
      newExpanded.delete(item)
    } else {
      newExpanded.add(item)
    }
    setExpandedItems(newExpanded)
  }

  const handleItemClick = (item: MetaObjective | TiktokGoal) => {
    if (isMeta) {
      onObjectiveSelect(item as MetaObjective)
    } else {
      // For TikTok, treat goals as optimization goals and call onGoalSelect
      onGoalSelect(item as TiktokGoal)
    }
  }

  const title = isMeta
    ? `Select Objective for ${adChannel.replace(/_/g, " ").toLowerCase()}`
    : `Select Goal for ${adChannel.replace(/_/g, " ").toLowerCase()}`

  const itemDescription = isMeta
    ? `${adChannel} objective`
    : `${adChannel} goal`

  return (
    <motion.div
      key="objective-selection"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{
        opacity: 0,
        transition: {
          duration: 0.15,
          ease: "easeInOut",
        },
      }}
      transition={{
        duration: 0.2,
        ease: "easeInOut",
      }}
      className="space-y-3 px-system-md mt-system-md"
    >
      <div className="text-lg font-semibold text-white mb-3">{title}</div>
      <div className="grid grid-cols-1 gap-2">
        {items.map((item, index) => {
          const spending = getSpendingForItem(item)
          const campaigns = getCampaignsForItem(item)
          const isExpanded = expandedItems.has(item)

          return (
            <div key={item}>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{
                  opacity: 0,
                  y: -5,
                  transition: {
                    duration: 0.1,
                    delay: index * 0.02,
                  },
                }}
                transition={{
                  duration: 0.15,
                  delay: index * 0.05,
                }}
                className="bg-system-blue-600/20 hover:bg-system-blue-600/30 rounded-md border border-system-blue-600/30 hover:border-system-blue-400/50 transition-all duration-200"
              >
                <div className="flex items-center gap-3 p-system-sm">
                  <div className="w-6 h-6 rounded-md bg-system-blue-600/30 flex items-center justify-center transition-colors">
                    {(() => {
                      const IconComponent = isMeta
                        ? META_OBJECTIVE_ICONS[item]
                        : undefined
                      return IconComponent ? (
                        <IconComponent className="w-4 h-4 text-system-blue-400" />
                      ) : (
                        <RocketLaunchIcon className="w-4 h-4 text-system-blue-400" />
                      )
                    })()}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="text-white font-medium text-base">
                        {item.replace(/_/g, " ").toLowerCase()}
                      </div>
                      {spending > 0 && (
                        <div className="text-system-cyan-200 font-semibold text-sm text-right">
                          Spend {toCurrency(spending)} last 30 days,{" "}
                          {totalChannelSpend > 0
                            ? ((spending / totalChannelSpend) * 100).toFixed(1)
                            : 0}
                          % of total spend
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-system-gray-400">
                        {itemDescription}
                      </div>
                      <div className="flex items-center gap-2">
                        {campaigns.length > 0 && (
                          <button
                            onClick={e => {
                              e.stopPropagation()
                              toggleExpanded(item)
                            }}
                            className="text-system-gray-400 hover:text-white transition-colors p-1"
                          >
                            {isExpanded ? (
                              <ChevronDownIcon className="w-4 h-4" />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handleItemClick(item)}
                          className="text-system-blue-400 hover:text-system-blue-300 transition-colors text-sm font-medium"
                        >
                          Select
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Campaigns List */}
                {isExpanded && campaigns.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border-t border-system-blue-600/30 bg-system-blue-600/10"
                  >
                    <div className="p-3 space-y-2">
                      <div className="text-xs text-system-gray-400 font-medium uppercase tracking-wide">
                        Campaigns ({campaigns.length})
                      </div>
                      {campaigns.map(campaign => (
                        <div
                          key={campaign.id}
                          className="flex items-center justify-between py-1 px-2 rounded bg-system-blue-600/20"
                        >
                          <div className="text-sm text-white truncate">
                            {campaign.name || `Campaign ${campaign.id}`}
                          </div>
                          <div className="text-xs text-system-cyan-200 font-medium">
                            {toCurrency(campaign.spend)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </div>
          )
        })}
      </div>
      {error && touched && (
        <p className="text-red-400 text-base mt-2">{error}</p>
      )}
    </motion.div>
  )
}
